/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { CustomFontVariantUrl } from '../elements/types';
import type { FontMetricOverrides } from '../elements/text/utils/selectFontMetrics';

type FontFormat = 'woff' | 'woff2' | 'truetype' | 'opentype';

function escapeCssString(value: unknown): string {
  return String(value).replace(
    /[\\'"<>\u0000-\u001f\u007f]/g,
    (character) => `\\${character.charCodeAt(0).toString(16)} `
  );
}

/**
 * Get the CSS font format for a given font URL.
 *
 * @param src Font URL.
 * @return Font format string or null if the font is unsupported.
 */
function getFontFormat(src: string): FontFormat | null {
  if (typeof src !== 'string') {
    return null;
  }
  const fileExtension = src.split(/[#?]/)?.[0]?.split('.')?.pop()?.trim();

  switch (fileExtension) {
    case 'woff':
    case 'woff2':
      return fileExtension;
    case 'ttf':
      return 'truetype';
    case 'otf':
      return 'opentype';
    default:
      return null;
  }
}

function getMimeType(format: FontFormat): string {
  switch (format) {
    case 'truetype':
      return 'font/ttf';
    case 'opentype':
      return 'font/otf';
    default:
      return `font/${format}`;
  }
}

function getFontFace({
  name,
  url,
  format,
  fontStyle,
  fontWeight,
  metricOverrides,
}: {
  name: string;
  url: string;
  format: string;
  fontStyle?: string;
  fontWeight?: number;
  metricOverrides?: FontMetricOverrides | null;
}) {
  const safeFontWeight =
    typeof fontWeight === 'number' &&
    Number.isFinite(fontWeight) &&
    fontWeight >= 1 &&
    fontWeight <= 1000
      ? fontWeight
      : 'normal';
  return `@font-face {
        font-family: "${escapeCssString(name)}";
        src: url('${escapeCssString(url)}') format('${escapeCssString(
    format
  )}');
        ${fontStyle ? `font-style: ${fontStyle};` : ''}
        font-weight: ${safeFontWeight};
        ${
          metricOverrides
            ? `ascent-override: ${metricOverrides.ascentOverride};
        descent-override: ${metricOverrides.descentOverride};
        line-gap-override: ${metricOverrides.lineGapOverride};`
            : ''
        }
        font-display: swap;
      }`;
}

/**
 * Get the inline stylesheet for a specific font family.
 *
 * @param name Font family.
 * @param url Font URL.
 * @param urls Font URLs (for custom fonts with variants)
 * @return Stylesheet or null if the font has an unsupported format.
 */
function getFontCSS(
  name: string,
  url: string,
  variants: CustomFontVariantUrl[] = [],
  metricOverrides?: FontMetricOverrides | null
): string | null {
  if (typeof name !== 'string' || !Array.isArray(variants)) {
    return null;
  }
  if (variants.length > 0) {
    let css = '';

    if (
      !variants.some(({ variant }) => variant[0] === 0 && variant[1] === 400)
    ) {
      const baseFormat = getFontFormat(url);
      if (baseFormat) {
        css += getFontFace({
          name,
          url,
          format: baseFormat,
          fontStyle: 'normal',
          fontWeight: 400,
          metricOverrides,
        });
      }
    }

    for (const { url, variant } of variants) {
      const format = getFontFormat(url);

      if (!format) {
        continue;
      }

      const fontWeight = variant[1];
      const fontStyle = variant[0] === 1 ? 'italic' : 'normal';

      css += getFontFace({
        name,
        url,
        format,
        fontStyle,
        fontWeight,
        metricOverrides,
      });
    }

    return css;
  }

  if (typeof url !== 'string') {
    return null;
  }

  const format = getFontFormat(url);

  if (!format) {
    return null;
  }

  return getFontFace({ name, url, format, metricOverrides });
}

export async function getDownloadedFontCSS(
  name: string,
  url: string,
  variants: CustomFontVariantUrl[] = [],
  metricOverrides?: FontMetricOverrides | null
): Promise<string | null> {
  if (variants.length > 0) {
    let css = '';

    if (
      !variants.some(({ variant }) => variant[0] === 0 && variant[1] === 400)
    ) {
      const baseFormat = getFontFormat(url);
      if (baseFormat) {
        const dataUri = await getFontDataUri(url, getMimeType(baseFormat));
        css += getFontFace({
          name,
          url: dataUri,
          format: baseFormat,
          fontStyle: 'normal',
          fontWeight: 400,
          metricOverrides,
        });
      }
    }

    for (const { url, variant } of variants) {
      const format = getFontFormat(url);

      if (!format) {
        continue;
      }

      const dataUri = await getFontDataUri(url, getMimeType(format));

      const fontWeight = variant[1];
      const fontStyle = variant[0] === 1 ? 'italic' : 'normal';

      css += getFontFace({
        name,
        url: dataUri,
        format,
        fontStyle,
        fontWeight,
        metricOverrides,
      });
    }

    return css;
  }

  const format = getFontFormat(url);

  if (!format) {
    return null;
  }

  const dataUri = await getFontDataUri(url, getMimeType(format));

  return getFontFace({ name, url: dataUri, format, metricOverrides });
}

async function getFontDataUri(url: string, mime = 'font/woff2') {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const blob = new Blob([buf], { type: mime });

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Unable to convert font to data URI'));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Unable to read font blob'));
    };
    reader.readAsDataURL(blob);
  });
}

export default getFontCSS;
